import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import { getActuatorNumberOfChannels, getUsedActuatorChannels } from '../../Functions/ActuatorChannels';
import { addPublicURL } from '../../Functions/AddPublicURL';
import { DISTRIBUTION_BOARD_EXPORT_TARGET_ID } from '../../Functions/ExportToPdf';
import { getIconPath } from '../../Functions/GetIconPath';
import { getFromLocalStorage, saveToLocalStorage } from '../../Functions/LocalStorageUtils';
import { setSelectedActuator } from '../../redux/reducers/actuatorsRedurer';
import { resetConfiguration, setActuators, setAppliances, setBottomBarOpen, setFloorplanImage, setOtherDevices, setShowActuatorDetail, setTotalDINModules } from '../../redux/reducers/appReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { ActuatorStateI } from '../../redux/types/app';
import Button from '../Button/Button';
import UnsavedChangesPopup from '../UnsavedChangesPopup/UnsavedChangesPopup';

import styles from './BottomBar.module.scss';

export interface BottomBarPropsI {
  children?: React.ReactNode;
  bottomDrawerOpen?: boolean;
}

const BottomBar = ({ children, bottomDrawerOpen }: BottomBarPropsI): JSX.Element => {
  return (
    <>
      <div
        id="tutorial-bottom-menu"
        className={classNames(styles.BottomBar, {
          [styles.BottomBar__open]: bottomDrawerOpen
        })}
      >
        {children}
      </div>
      <div className={styles.BottomBar__version}>
        <span>BETA v{process.env.REACT_APP_VERSION} (Only Wireless tech)</span>
        <a href="mailto:t.kapitanov@elkoep.com" target="_blank" rel="noopener noreferrer">Report an issue</a>
        <UploadFloorplanIcon />
      </div>
    </>
  );
};

const Opener: React.FC<{ children: React.ReactNode; bottomDrawerOpen?: boolean }> = ({ children, bottomDrawerOpen }) => {
  const dispatch = useAppDispatch();
  const rightMenuShown = useAppSelector((state) => state.app.rightMenuShown);
  const modules = useAppSelector((state) => state.app.totalDINModules);
  const dinActuators = useAppSelector((state) => state.app.actuators).filter(actuator => actuator.dinrail === true);
  const otherDevices = useAppSelector((state) => state.app.otherDevices);
  if(bottomDrawerOpen) return null;

  const calculateModules = () => {
    const actuatorModules = dinActuators.reduce((total, actuator) => actuator.modules ? total + actuator.modules : total, 0);
    const otherModules = otherDevices.reduce((total, device) => total + (device.modules ?? 0), 0);
    return actuatorModules + otherModules;
  }

  const openActuatorDetail = (actuator: ActuatorStateI) => {
    dispatch(setSelectedActuator(actuator));
    dispatch(setShowActuatorDetail(true));
  }

  return (
    <>
      <div className={classNames(styles.BottomBar__actuatorIcons, {
        [styles.BottomBar__actuatorIcons__rightMenuShown]: rightMenuShown
      })}>
        {dinActuators.map(actuator => (
          <div key={actuator.id} className={styles.BottomBar__actuatorIcons__item}>
            <div className={styles.BottomBar__actuatorIcons__item__countIndicator}>
              {(() => {
                const usedChannels = getUsedActuatorChannels(actuator);
                const totalChannels = getActuatorNumberOfChannels(actuator);
                return `${usedChannels}/${totalChannels}`;
              })()}
            </div>
            <img onClick={() => { openActuatorDetail(actuator) }} src={getIconPath(actuator.icon, '.svg', 'actuators')} alt={actuator.name} />
          </div>
        ))}
      </div>
      <div className={classNames(styles.BottomBar__opener, {
        [styles.BottomBar__opener__rightMenuShown]: rightMenuShown
      })}>
        <span className={styles.BottomBar__opener__modules}><p>{calculateModules()}/{modules}</p></span>
        {children}
      </div>
    </> 
    
  );
}

const DinRail: React.FC = ({}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const railRef = useRef<HTMLDivElement | null>(null);

  const calculateDinRailModules = () => {
    const rail = railRef.current;
    if (!rail) {
      return 0;
    }
    const railWidth = rail.offsetWidth;
    const moduleWidth = 67; // assuming each module takes 67px width
    return Math.floor(railWidth / moduleWidth);
  }

  useEffect(() => {
    const totalModules = calculateDinRailModules();
    if(totalModules === 0) return;
    dispatch(setTotalDINModules(totalModules));
  }, [railRef, dispatch]);

  return (
    <div className={styles.BottomBar__dinRail}>
      <div className={styles.BottomBar__dinRail__header}>
        <div className={styles.BottomBar__dinRail__header__back}>
          <img src={getIconPath('arrow_back')} alt="Back" onClick={() => dispatch(setBottomBarOpen(false))}/>
          <Button variant='fill' size='md' onClick={() => dispatch(setBottomBarOpen(false))}>{t('electrical_cabinet.apply')}</Button>
        </div>
        <h3>{t('electrical_cabinet.heading')}</h3>
        <p>{t('electrical_cabinet.description')}</p>
        <p>{t('electrical_cabinet.reorderDescription')}</p>
      </div>
      <div className={styles.BottomBar__dinRail__content}>
        <div className={styles.BottomBar__dinRail__content__background}>
          <img src={addPublicURL('assets/electrical_cabinet.png')} alt="Electrical Cabinet" />
        </div>
        <div 
            className={styles.BottomBar__dinRail__content__rail}
          ref={railRef}
            style={{
              backgroundImage: `url('${addPublicURL('assets/DIN_gray_seamless.png')}')`,
              backgroundRepeat: 'repeat-x',
              backgroundSize: 'auto 100%',
              backgroundPosition: 'left center',
              zIndex: 1,
            }}
        ></div>
      </div>
    </div>
  );
}

const Actuators: React.FC<{ exportOnly?: boolean }> = ({ exportOnly = false }) => {
  const actuators = useAppSelector((state) => state.app.actuators);
  const appliances = useAppSelector((state) => state.app.appliances);
  const otherDevices = useAppSelector((state) => state.app.otherDevices);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const railRef = useRef<HTMLDivElement | null>(null);
  const [visualOrder, setVisualOrder] = useState<string[]>([]);
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dropBeforeKey, setDropBeforeKey] = useState<string | null>(null);
  const draggedItemRef = useRef<{ key: string; kind: 'actuator' | 'other'; id: number } | null>(null);
  const droppedOnRailRef = useRef(false);
  const cancelledDragRef = useRef(false);
  const visualOrderStorageKey = 'dinRailVisualOrder';
  const railItems = useMemo(() => {
    const items = [
      ...actuators.filter((actuator) => actuator.dinrail).map((actuator) => ({
        key: `actuator-${actuator.id}-${actuator.type}`,
        kind: 'actuator' as const,
        id: actuator.id,
        name: actuator.name,
        image: addPublicURL(`assets/actuators/${actuator.refImg}`)
      })),
      ...otherDevices.map((device) => ({
        key: `other-${device.id}-${device.type}`,
        kind: 'other' as const,
        id: device.id,
        name: device.name,
        image: addPublicURL(`assets/other/${device.refImg}`)
      }))
    ];
    const itemsByKey = new Map(items.map((item) => [item.key, item]));
    return [
      ...visualOrder.flatMap((key) => {
        const item = itemsByKey.get(key);
        return item ? [item] : [];
      }),
      ...items.filter((item) => !visualOrder.includes(item.key))
    ];
  }, [actuators, otherDevices, visualOrder]);

  useEffect(() => {
    try {
      const savedOrder = getFromLocalStorage(visualOrderStorageKey);
      if (savedOrder) {
        const parsedOrder: unknown = JSON.parse(savedOrder);
        if (!Array.isArray(parsedOrder) || !parsedOrder.every((key): key is string => typeof key === 'string')) {
          throw new Error('Invalid DIN rail visual order.');
        }
        setVisualOrder(Array.from(new Set(parsedOrder)));
      }
    } catch (error) {
      console.error('Failed to load DIN rail visual order.', error);
      toast.error(t('electrical_cabinet.orderLoadFailed'));
    }
  }, [t]);

  useEffect(() => {
    if (!draggedKey) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        cancelledDragRef.current = true;
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [draggedKey]);

  const showActuatorDetail = (actuatorId: number) => {
      const actuator = actuators.find(a => a.id === actuatorId);
      if(!actuator){
          toast.error(t('rightMenu.actuators.error'));
          return;
      }
      dispatch(setSelectedActuator(actuator));
      dispatch(setShowActuatorDetail(true));
  }

  const deleteActuator = (actuatorId: number) => {
    dispatch(setActuators(actuators.filter((actuator) => actuator.id !== actuatorId)));

    dispatch(
      setAppliances(
        appliances.map((appliance) => {
          if (appliance.actuator === actuatorId) {
            const nextAppliance = { ...appliance };
            nextAppliance.actuator = undefined;
            return nextAppliance;
          }
          return appliance;
        })
      )
    );

    dispatch(setShowActuatorDetail(false));
  };

  const deleteOtherDevice = (deviceId: number) => {
    dispatch(setOtherDevices(otherDevices.filter((device) => device.id !== deviceId)));
  };

  const handleRailDragStart = (event: React.DragEvent<HTMLImageElement>, item: typeof railItems[number]) => {
    if (item.id === undefined) {
      event.preventDefault();
      return;
    }
    draggedItemRef.current = { key: item.key, kind: item.kind, id: item.id };
    droppedOnRailRef.current = false;
    cancelledDragRef.current = false;
    setDraggedKey(item.key);
    event.dataTransfer.setData('application/dinrail-item-key', item.key);
    if (item.kind === 'actuator') {
      event.dataTransfer.setData('application/dinrail-actuator-id', `${item.id}`);
    }
    event.dataTransfer.effectAllowed = 'move';
  };

  const getDropBeforeKey = (clientX: number) => {
    const itemElements = railRef.current?.querySelectorAll<HTMLElement>('[data-dinrail-key]');
    if (!itemElements) {
      return null;
    }
    for (const element of Array.from(itemElements)) {
      const key = element.dataset.dinrailKey;
      if (key && key !== draggedItemRef.current?.key) {
        const rect = element.getBoundingClientRect();
        if (clientX < rect.left + rect.width / 2) {
          return key;
        }
      }
    }
    return null;
  };

  const handleRailDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!draggedItemRef.current) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropBeforeKey(getDropBeforeKey(event.clientX));
  };

  const handleRailDrop = (event: React.DragEvent<HTMLDivElement>) => {
    const draggedItem = draggedItemRef.current;
    if (!draggedItem) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    droppedOnRailRef.current = true;
    const nextOrder = railItems.map((item) => item.key).filter((key) => key !== draggedItem.key);
    const beforeKey = getDropBeforeKey(event.clientX);
    const insertionIndex = beforeKey === null ? nextOrder.length : nextOrder.indexOf(beforeKey);
    nextOrder.splice(insertionIndex, 0, draggedItem.key);
    setVisualOrder(nextOrder);
    setDropBeforeKey(null);
    try {
      saveToLocalStorage(visualOrderStorageKey, JSON.stringify(nextOrder));
    } catch (error) {
      console.error('Failed to save DIN rail visual order.', error);
      toast.error(t('electrical_cabinet.orderSaveFailed'));
    }
  };

  const handleRailDragEnd = (event: React.DragEvent<HTMLImageElement>) => {
    const draggedItem = draggedItemRef.current;
    draggedItemRef.current = null;
    setDraggedKey(null);
    setDropBeforeKey(null);
    const rail = railRef.current;
    if (!rail || !draggedItem || droppedOnRailRef.current || cancelledDragRef.current || (event.clientX === 0 && event.clientY === 0)) {
      return;
    }

    const railRect = rail.getBoundingClientRect();
    const droppedOutsideRail =
      event.clientX < railRect.left ||
      event.clientX > railRect.right ||
      event.clientY < railRect.top ||
      event.clientY > railRect.bottom;

    if (droppedOutsideRail) {
      if (draggedItem.kind === 'actuator') {
        deleteActuator(draggedItem.id);
      } else {
        deleteOtherDevice(draggedItem.id);
      }
    }
  };

  const handleActuatorRefMouseEnter = (event: React.MouseEvent<HTMLImageElement>) => {
    event.currentTarget.parentElement?.classList.add(styles.BottomBar__actuatorsRef__content__item__hover);
  };

  const handleActuatorRefMouseLeave = (event: React.MouseEvent<HTMLImageElement>) => {
    event.currentTarget.parentElement?.classList.remove(styles.BottomBar__actuatorsRef__content__item__hover);
  };

  return (
    <>
    <div id={DISTRIBUTION_BOARD_EXPORT_TARGET_ID} className={styles.BottomBar__pdfBoard} aria-hidden="true">
      <img className={styles.BottomBar__pdfBoard__cabinet} src={addPublicURL('assets/electrical_cabinet.png')} alt="" />
      <div className={styles.BottomBar__pdfBoard__rail} style={{ backgroundImage: `url('${addPublicURL('assets/DIN_gray_seamless.png')}')` }} />
      <div className={styles.BottomBar__pdfBoard__devices}>
        {railItems.map((item) => <img key={item.key} src={item.image} alt={item.name} />)}
      </div>
    </div>
    {!exportOnly && <>
    <div className={styles.BottomBar__actuators}>
      <div className={styles.BottomBar__actuators__content}>
        {railItems.map((item) => (
            <div key={item.key} className={styles.BottomBar__actuators__content__item}>
              <img
                draggable={item.id !== undefined}
                src={item.image}
                alt={item.name}
                onDragStart={(event) => handleRailDragStart(event, item)}
                onDragEnd={handleRailDragEnd}
                onClick={item.kind === 'actuator' ? () => showActuatorDetail(item.id ?? 0) : undefined}
              />
            </div>
        ))}
      </div>
    </div>
    <div className={styles.BottomBar__actuatorsRef}>
      <div ref={railRef} className={styles.BottomBar__actuatorsRef__content} onDragOver={handleRailDragOver} onDrop={handleRailDrop}>
        {railItems.map((item, index) => (
            <div
              key={item.key}
              data-dinrail-key={item.key}
              className={classNames(styles.BottomBar__actuatorsRef__content__item, {
                [styles.BottomBar__actuatorsRef__content__item__dragging]: draggedKey === item.key,
                [styles.BottomBar__actuatorsRef__content__item__dropBefore]: draggedKey !== null && dropBeforeKey === item.key,
                [styles.BottomBar__actuatorsRef__content__item__dropAfter]: draggedKey !== null && dropBeforeKey === null && index === railItems.length - 1
              })}
            >
              <img
                draggable={item.id !== undefined}
                src={item.image}
                alt={item.name}
                onDragStart={(event) => handleRailDragStart(event, item)}
                onMouseEnter={handleActuatorRefMouseEnter}
                onMouseLeave={handleActuatorRefMouseLeave}
                onDragEnd={handleRailDragEnd}
                onClick={item.kind === 'actuator' ? () => showActuatorDetail(item.id ?? 0) : undefined}
              />
            </div>
        ))}
      </div>
    </div>
    </>}
    </>
  );
}

const UploadFloorplanIcon: React.FC = ({}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [hover, setHover] = useState(false);
  const [pendingFloorplanImage, setPendingFloorplanImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const leftMenuShown = useAppSelector((state) => state.app.leftMenuShown);

  const handleUploadFloorplanClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFloorplanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith('image/')) {
      toast.error(t('floorplan.upload.invalidType'));
      event.target.value = '';
      return;
    }

    const maxSizeInBytes = 8 * 1024 * 1024;
    if (selectedFile.size > maxSizeInBytes) {
      toast.error(t('floorplan.upload.tooLarge'));
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        toast.error(t('floorplan.upload.failed'));
        return;
      }

      setPendingFloorplanImage(result);
    };

    reader.onerror = () => {
      toast.error(t('floorplan.upload.failed'));
    };

    reader.readAsDataURL(selectedFile);
    event.target.value = '';
  };

  const confirmFloorplanUpload = () => {
    if (!pendingFloorplanImage) {
      return;
    }

    dispatch(resetConfiguration());
    dispatch(setFloorplanImage(pendingFloorplanImage));
    saveToLocalStorage('custom_floorplan_image', pendingFloorplanImage);
    setPendingFloorplanImage(null);
    toast.success(t('floorplan.upload.success'));
  };

  const handleResetFloorplan = () => {
    dispatch(setFloorplanImage(null));
    saveToLocalStorage('custom_floorplan_image', '');
    toast.success(t('floorplan.upload.resetSuccess'));
  };

  React.useEffect(() => {
    const storedImage = getFromLocalStorage('custom_floorplan_image');
    if (storedImage) {
      dispatch(setFloorplanImage(storedImage));
    }
  }, [dispatch]);

  return (
    <div
      className={classNames(styles.BottomBar__uploadFloorplanIcon, {
        [styles.BottomBar__uploadFloorplanIcon__leftMenuShown]: leftMenuShown,
      })}
    
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUploadFloorplanChange}
        style={{ display: 'none' }}
      />
      <div
        className={styles.BottomBar__uploadFloorplanIcon__item}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={handleUploadFloorplanClick}
      >
        <img src={hover ? getIconPath('add_photo') : getIconPath('add_photo_active')} alt="Upload Floorplan"/>
      </div>
      <button
        type="button"
        className={styles.BottomBar__uploadFloorplanIcon__reset}
        onClick={handleResetFloorplan}
      >
        {t('floorplan.upload.reset')}
      </button>
      {pendingFloorplanImage && (
        <UnsavedChangesPopup
          title="floorplan.upload.confirm.title"
          description="floorplan.upload.confirm.description"
          stayLabel="floorplan.upload.confirm.cancel"
          leaveLabel="floorplan.upload.confirm.proceed"
          onStay={() => setPendingFloorplanImage(null)}
          onLeave={confirmFloorplanUpload}
        />
      )}
    </div>
  )
}

BottomBar.opener = Opener;
BottomBar.dinRail = DinRail;
BottomBar.actuators = Actuators;
BottomBar.UploadFloorplanIcon = UploadFloorplanIcon;
export default BottomBar;
